/**
 * Навігація оболонки з метаданих: після входу — `/meta` платформи (пункти модулів за правами,
 * стартове подання, «Адміністрування»); поки `/meta` немає (вантажиться, сесія скінчилась) — «На
 * головну» і «Увійти».
 */
import { useMemo } from 'react';
import { adminEntry, hasReferences, REFERENCES_PAGE } from '../features/admin/registry.ts';
import { myTasksView } from '../features/tasks/views.ts';
import { todayAvailable } from '../features/today/source.ts';
import { shell as t } from '../i18n/uk/shell.ts';
import { usePlatformMeta } from '../platform/meta.ts';
import { navItems } from '../platform/modules.ts';
import { platformText } from '../platform/text.ts';
import { usePrefs } from '../prefs/prefs.ts';
import {
  ADMIN_PATH,
  buildNav,
  guestNav,
  type NavEntry,
  type ShellNav,
  viewPath,
  withPersonalStart,
} from './nav-model.ts';

export interface ShellNavState {
  nav: ShellNav;
  /** Метадані платформи є — людина увійшла (за ними й побудовано навігацію). */
  signedIn: boolean;
  /** Відповідь `/meta` успішна — можна відкривати потік подій SSE. */
  metaReady: boolean;
  /** Після входу: модуль задач увімкнено й людина читає задачі — панель «Сьогодні» є. */
  tasks: boolean;
}

export function useShellNav(): ShellNavState {
  // Без ознаки входу оболонка в API не ходить: перший екран лишається без запитів до сервера.
  const platform = usePlatformMeta({ optional: true });
  const { startPage } = usePrefs();
  const nav = useMemo(() => {
    if (platform.data) {
      const entry = adminEntry(platform.data);
      // Лише «Довідники» (людина веде довідник без адмін-прав): пункт веде прямо на сторінку,
      // «Налаштувань» немає. Інакше «Довідники» — всередині, а в палітрі ще й окремою командою.
      const references: NavEntry = {
        id: 'references',
        to: `${ADMIN_PATH}/${REFERENCES_PAGE}`,
        label: t.navReferences,
        icon: 'list',
        exact: false,
      };
      const built =
        entry === 'references'
          ? buildNav(platform.data, navItems(platform.data), t.navReferencesRail, references.to)
          : buildNav(
              platform.data,
              navItems(platform.data),
              entry === 'section' ? t.navAdminShort : null,
              ADMIN_PATH,
              entry === 'section' && hasReferences(platform.data) ? [references] : [],
            );
      // «Мої задачі» (E8a-W2) — перехід палітри, якщо меню компанії не має такого пункту.
      const tasks = myTasksView(platform.data);
      const tasksTo = tasks ? viewPath(tasks.key) : null;
      const listed = [...built.items, ...(built.more ?? [])].some((item) => item.to === tasksTo);
      const withTasks: ShellNav =
        tasks && tasksTo && !listed
          ? {
              ...built,
              extra: [
                ...(built.extra ?? []),
                { id: 'my-tasks', to: tasksTo, label: t.navMyTasks, icon: 'task', exact: true },
              ],
            }
          : built;
      // Стартова з профілю (PRF-06): логотип і `G` → `H` ведуть туди ж, куди «/».
      return withPersonalStart(withTasks, startPage).nav;
    }
    return guestNav(t.navHome, platformText.signInLink);
  }, [platform.data, startPage]);
  return {
    nav,
    signedIn: Boolean(platform.data),
    metaReady: platform.isSuccess,
    tasks: platform.data ? todayAvailable(platform.data) : false,
  };
}
